import React, { useState } from 'react';
import {
  chipStyle,
  chipWrapStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  summaryCardStyle,
  summaryGridStyle,
  summaryLabelStyle,
  summaryValueStyle,
} from './dashboardStyles';
import SaasReviewDecisionPanel from './SaasReviewDecisionPanel';

const DECISION_FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'module_unmapped', label: '모듈 미연결' },
  { key: 'module_map_candidate', label: '연결 후보' },
  { key: 'package_hr', label: '인사 패키지' },
  { key: 'package_finance', label: '금액 패키지' },
  { key: 'package_security', label: '플랫폼 보안' },
  { key: 'first_product', label: '1차 후보' },
  { key: 'platform_core', label: '플랫폼 운영' },
  { key: 'later_package', label: '2차/별도' },
  { key: 'internal_only', label: '내부 전용' },
  { key: 'exclude', label: '제외' },
  { key: 'review', label: '검토' },
];

const decisionTone = {
  first_product: '#2563eb',
  platform_core: '#7c3aed',
  later_package: '#0f766e',
  internal_only: '#64748b',
  exclude: '#dc2626',
  review: '#d97706',
};

const riskTone = {
  low: '#16a34a',
  medium: '#2563eb',
  high: '#d97706',
  critical: '#dc2626',
};

const readinessTone = {
  ready: '#16a34a',
  needs_work: '#d97706',
  blocked: '#dc2626',
  hold: '#64748b',
};

function badgeStyle(color) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 6,
    border: `1px solid ${color}`,
    color,
    background: 'var(--bg-card)',
    padding: '2px 6px',
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  };
}

function filterButtonStyle(active) {
  return {
    border: `1px solid ${active ? '#2563eb' : 'var(--border)'}`,
    borderRadius: 7,
    background: active ? 'rgba(37,99,235,0.12)' : 'var(--bg-card)',
    color: active ? '#2563eb' : 'var(--text-sub)',
    padding: '5px 8px',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

function SummaryCard({ label, value, help }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value ?? '-'}</div>
      {help && <div style={{ ...rowMetaStyle, marginTop: 4 }}>{help}</div>}
    </div>
  );
}

function LaunchCheckList({ row }) {
  const items = [
    ...(row.launchPlan?.launchBlockers || []),
    ...(row.launchPlan?.launchChecks || []),
  ].slice(0, 3);
  if (items.length === 0) {
    return <div style={rowMetaStyle}>추가 확인 항목 없음</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {items.map((item) => (
        <div key={`${row.code}-${item.code}`} style={{ ...rowMetaStyle, lineHeight: 1.35 }}>
          <strong style={{ color: 'var(--text)' }}>{item.label}</strong>
          <br />
          {item.detail}
        </div>
      ))}
    </div>
  );
}

export default function SaasReadinessBaseline({ baseline }) {
  const [decisionFilter, setDecisionFilter] = useState('all');
  const rows = baseline?.rows || [];
  const filteredRows = rows.filter((row) => {
    if (decisionFilter === 'all') return true;
    if (decisionFilter === 'module_unmapped') return !row.moduleMapped;
    if (decisionFilter === 'module_map_candidate') return row.moduleRecommendation?.status === 'map_candidate';
    if (decisionFilter.startsWith('package_')) {
      return row.moduleRecommendation?.package?.code === decisionFilter.replace('package_', '');
    }
    return row.decision === decisionFilter;
  });

  if (!baseline) return null;
  const productName = baseline.productName || 'MeetingLink';
  const productNameKo = baseline.productNameKo || '미팅링크';

  return (
    <section style={{ marginTop: 12 }}>
      <div style={{ ...summaryCardStyle, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h3 style={panelTitleStyle}>{productName} SaaS 전환 기준표</h3>
            <div style={panelSubStyle}>현재 DB 권한과 업무/기능 배정 기준으로 만든 {productNameKo} 읽기 전용 진단표</div>
          </div>
          <div style={rowMetaStyle}>생성 기준 {baseline.generatedAt || '-'}</div>
        </div>

        <div style={{ ...summaryGridStyle, marginTop: 10 }}>
          <SummaryCard label="바로 검증" value={baseline.summary?.launchReadyCount} help="기존 권한과 비교 검증 가능한 항목" />
          <SummaryCard label="작업 필요" value={baseline.summary?.launchNeedsWorkCount} help="출시 전 보완이 필요한 항목" />
          <SummaryCard label="출시 차단" value={baseline.summary?.launchBlockedCount} help="민감정보/DB 등 고객사 노출 금지 후보" />
          <SummaryCard label="보류" value={baseline.summary?.launchHoldCount} help="1차 상품에서 제외하거나 뒤로 미룰 항목" />
          <SummaryCard label="1차 후보" value={baseline.summary?.firstProductCount} help={`${productNameKo} 첫 제품에 넣을 기능`} />
          <SummaryCard label="플랫폼 운영" value={baseline.summary?.platformCoreCount} help="PINE 운영자/고객사 관리 기준" />
          <SummaryCard label="2차/별도" value={baseline.summary?.laterPackageCount} help="나중에 패키지로 분리할 기능" />
          <SummaryCard label="내부/제외" value={(baseline.summary?.internalOnlyCount || 0) + (baseline.summary?.excludeCount || 0)} help="1차 SaaS에서 빼야 할 기능" />
          <SummaryCard label="보안 위험" value={baseline.summary?.highRiskCount} help="높음 이상 위험 권한" />
          <SummaryCard label="회사별 권한" value={baseline.summary?.companyScopedCount} help="tenant 기능 배정으로 바꿀 후보" />
          <SummaryCard label="모듈 미연결" value={baseline.summary?.moduleUnmappedCount} help="아직 SaaS 기능 묶음에 없는 권한" />
          <SummaryCard label="연결 후보" value={baseline.summary?.moduleMapCandidateCount} help="기존 모듈에 붙일 수 있는 권한" />
          <SummaryCard label="검토 결정" value={baseline.summary?.reviewDecisionCount} help="사람이 판단할 항목" />
        </div>

        {(baseline.summary?.launchBlockerSummaries || []).length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={rowTitleStyle}>출시 전 막히는 이유</div>
            <div style={chipWrapStyle}>
              {baseline.summary.launchBlockerSummaries.map((item) => (
                <span key={item.code} style={chipStyle}>
                  {item.label} · {item.count}개
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <div style={rowTitleStyle}>추천 패키지 요약</div>
          <div style={chipWrapStyle}>
            {(baseline.summary?.packageSummaries || []).map((pkg) => (
              <span key={pkg.code} style={chipStyle}>
                {pkg.name} · {pkg.count}개 · {pkg.stage}
              </span>
            ))}
          </div>
        </div>

        <SaasReviewDecisionPanel items={baseline.reviewDecisionItems || []} />

        <div style={{ marginTop: 12 }}>
          <div style={rowTitleStyle}>현재 tenant 후보</div>
          <div style={chipWrapStyle}>
            {(baseline.tenantsPreview || []).map((tenant) => (
              <span key={tenant.code} style={chipStyle} title={tenant.note}>
                {tenant.code} · {tenant.role}
              </span>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DECISION_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              style={filterButtonStyle(decisionFilter === filter.key)}
              onClick={() => setDecisionFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1380 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-sub)', fontSize: 11 }}>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>메뉴/기능</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>범위</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>SaaS 모듈</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>추천</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>출시 상태</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>출시 전 확인</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>판단</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>재사용</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>위험</th>
                <th style={{ textAlign: 'left', padding: '7px 6px' }}>이유</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.code} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    <div style={rowTitleStyle}>{row.label}</div>
                    <div style={rowMetaStyle}>{row.code}</div>
                    <div style={rowMetaStyle}>{row.source}</div>
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    <span style={badgeStyle(row.companyCode ? '#0f766e' : '#64748b')}>
                      {row.scopeLabel}{row.companyCode ? ` · ${row.companyCode}` : ''}
                    </span>
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    {row.moduleMapped ? (
                      <div style={chipWrapStyle}>
                        {row.modules.map((module) => (
                          <span key={`${row.code}-${module.code}`} style={chipStyle} title={module.code}>
                            {module.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={badgeStyle('#d97706')}>미연결</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top', minWidth: 170 }}>
                    <span style={badgeStyle(row.moduleRecommendation?.status === 'map_candidate' ? '#2563eb' : '#64748b')}>
                      {row.moduleRecommendation?.statusLabel || '검토 필요'}
                    </span>
                    {row.moduleRecommendation?.targetModuleName && (
                      <div style={{ ...rowMetaStyle, marginTop: 4 }}>
                        → {row.moduleRecommendation.targetModuleName}
                      </div>
                    )}
                    {row.moduleRecommendation?.package?.name && (
                      <div style={{ ...rowMetaStyle, marginTop: 4 }}>
                        패키지: {row.moduleRecommendation.package.name} · {row.moduleRecommendation.package.stage}
                      </div>
                    )}
                    <div style={{ ...rowMetaStyle, marginTop: 4 }}>
                      {row.moduleRecommendation?.reason || '-'}
                    </div>
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top', minWidth: 150 }}>
                    <span style={badgeStyle(readinessTone[row.launchPlan?.readiness?.level] || '#64748b')}>
                      {row.launchPlan?.readiness?.label || '검토 필요'}
                    </span>
                    <div style={{ ...rowMetaStyle, marginTop: 5, color: 'var(--text)', fontWeight: 850 }}>
                      {row.launchPlan?.nextAction?.label || '-'}
                    </div>
                    <div style={{ ...rowMetaStyle, marginTop: 3, lineHeight: 1.35 }}>
                      {row.launchPlan?.nextAction?.detail || row.launchPlan?.shadowModeNote || '-'}
                    </div>
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top', minWidth: 230 }}>
                    <LaunchCheckList row={row} />
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    <span style={badgeStyle(decisionTone[row.decision] || '#64748b')}>{row.decisionLabel}</span>
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top', color: 'var(--text)', fontSize: 12, fontWeight: 800 }}>
                    {row.reuseLabel}
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    <span style={badgeStyle(riskTone[row.risk] || '#64748b')}>{row.riskLabel}</span>
                  </td>
                  <td style={{ padding: '8px 6px', verticalAlign: 'top', color: 'var(--text-sub)', fontSize: 12, fontWeight: 700, lineHeight: 1.45 }}>
                    {row.reason}
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ padding: 16, color: 'var(--text-sub)', textAlign: 'center', fontSize: 13 }}>
                    해당 분류의 권한이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
